import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import InvestorSidebar from '@/components/investor/InvestorSidebar';
import InvestorHomeTab from '@/components/investor/InvestorHomeTab';
import InvestorPortfolioTab from '@/components/investor/InvestorPortfolioTab';
import InvestorProfileTab from '@/components/investor/InvestorProfileTab';
import InvestorTransactionsTab from '@/components/investor/InvestorTransactionsTab';
import InvestorReportsTab from '@/components/investor/InvestorReportsTab';
import OrionFundTab from '@/components/investor/OrionFundTab';
import PropertyDetailContent from '@/components/investor/PropertyDetailContent';
import InvestmentBookingModal from '@/components/investor/InvestmentBookingModal';
import PropertyCard from '@/components/investor/PropertyCard';

export default function InvestorDashboardUnified({ user, setUser }) {
  const [activeTab, setActiveTab] = useState('home');
  const [opportunityFilter, setOpportunityFilter] = useState('open'); // 'open' or 'funded'
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showInvestmentFlow, setShowInvestmentFlow] = useState(false);
  const [investmentData, setInvestmentData] = useState(null);
  const [openProperties, setOpenProperties] = useState([]);
  const [fundedProperties, setFundedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch projects from API
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        // Fetch open opportunities
        const openResponse = await api.get('/projects/opportunities');
        const openProjects = openResponse.data || [];
        
        // Transform open properties
        const transformedOpen = openProjects
          .filter(p => p.unitCount > 0 || (p.financial && p.financial.unitValue > 0))
          .map(p => transformProject(p));
        
        setOpenProperties(transformedOpen);
        
        // Fetch funded opportunities
        const fundedResponse = await api.get('/projects/funded');
        const fundedProjects = fundedResponse.data || [];
        
        const transformedFunded = fundedProjects.map(p => ({
          ...transformProject(p),
          funding: 100,
          fundingPercentage: 100
        }));
        
        setFundedProperties(transformedFunded);
      } catch (error) {
        console.error('Error fetching properties:', error);
        setOpenProperties([]);
        setFundedProperties([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProperties();
  }, []);
  
  const transformProject = (p) => ({
    id: p.projectId,
    name: p.name,
    type: p.type || 'residence',
    location: p.location?.city || 'Unknown',
    sector: p.location?.sector || '',
    state: p.location?.state || '',
    value: p.financial?.unitValue || 0,
    capitalTarget: p.financial?.capitalTarget || 0,
    capitalRaised: p.financial?.capitalRaised || 0,
    funding: p.financial?.fundingPercentage || 0,
    fundingPercentage: p.financial?.fundingPercentage || 0,
    minInv: p.financial?.minInvestment || 500000,
    maxInv: p.financial?.maxInvestment || 10000000,
    financial: p.financial,
    images: p.images || [],
    image: p.images?.[0] || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600',
    unitCount: p.unitCount || 0,
    developer: p.developer?.name || 'Unknown Developer',
    rera: p.rera?.number || '',
    brokerage: p.brokerage,
    badges: p.badges || [],
    expectedROI: p.expectedROI || '35-40%',
    expectedTenure: p.expectedTenure || '2-3 years',
    advisor: p.advisor || null,
    // Pass through raw nested data for property detail
    _raw: p
  });
  
  // Use filtered properties based on opportunityFilter
  const properties = opportunityFilter === 'open' ? openProperties : fundedProperties;

  const formatINR = (val) => {
    if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val/100000).toFixed(0)}L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const handlePropertyClick = (propertyId) => {
    setSelectedProperty(propertyId);
    setShowInvestmentFlow(false);
  };

  const handleBackToGrid = () => {
    setSelectedProperty(null);
    setShowInvestmentFlow(false);
  };

  const handleBookInvestment = (propertyId, amount, unitId) => {
    setInvestmentData({ propertyId, amount, unitId });
    setShowInvestmentFlow(true);
  };

  const renderContent = () => {
    // If viewing project detail page
    if (selectedProperty) {
      const propertyData = properties.find(p => p.id === selectedProperty);
      return (
        <PropertyDetailContent 
          property={propertyData}
          onBack={handleBackToGrid}
          onBookInvestment={handleBookInvestment}
          unitLifecycleFilter={opportunityFilter}
        />
      );
    }

    // Regular tab content
    switch(activeTab) {
      case 'home':
        return <InvestorHomeTab user={user} setActiveTab={setActiveTab} />;
      
      case 'portfolio':
        return <InvestorPortfolioTab user={user} />;
      
      case 'profile':
        return <InvestorProfileTab user={user} />;
      
      case 'land':
        const landProperties = properties.filter(p => p.type === 'land');
        const openLandProperties = openProperties.filter(p => p.type === 'land');
        const fundedLandProperties = fundedProperties.filter(p => p.type === 'land');
        const displayedLandProperties = opportunityFilter === 'open' ? openLandProperties : fundedLandProperties;
        return (
          <div className="p-10">
            <h1 className="text-2xl font-light text-white mb-2">AltSlice · Land</h1>
            <p className="text-gray-500 text-sm mb-6">Select a project to view details and invest</p>
            
            {/* Open/Funded Tabs */}
            <div className="mb-6 flex gap-4 border-b border-white/10">
              <button
                onClick={() => setOpportunityFilter('open')}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  opportunityFilter === 'open' 
                    ? 'text-emerald-400 border-b-2 border-emerald-400' 
                    : 'text-gray-500 hover:text-white'
                }`}
                data-testid="land-tab-open"
              >
                Open ({openLandProperties.length})
              </button>
              <button
                onClick={() => setOpportunityFilter('funded')}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  opportunityFilter === 'funded' 
                    ? 'text-emerald-400 border-b-2 border-emerald-400' 
                    : 'text-gray-500 hover:text-white'
                }`}
                data-testid="land-tab-funded"
              >
                Funded ({fundedLandProperties.length})
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-[#4ADE80] border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-500">Loading properties...</p>
              </div>
            ) : displayedLandProperties.length === 0 ? (
              <div className="text-center py-20 border border-white/10">
                <p className="text-gray-500 mb-2">
                  {opportunityFilter === 'open' ? 'No open land opportunities' : 'No funded land opportunities yet'}
                </p>
                <p className="text-xs text-gray-600">
                  {opportunityFilter === 'open' ? 'Check back soon for new opportunities' : 'Funded deals will appear here'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayedLandProperties.map(property => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onClick={() => handlePropertyClick(property.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      
      case 'residences':
        const residenceProperties = properties.filter(p => p.type === 'residential' || p.type === 'residence');
        const openResidenceProperties = openProperties.filter(p => p.type === 'residential' || p.type === 'residence');
        const fundedResidenceProperties = fundedProperties.filter(p => p.type === 'residential' || p.type === 'residence');
        const displayedResidenceProperties = opportunityFilter === 'open' ? openResidenceProperties : fundedResidenceProperties;
        return (
          <div className="p-10">
            <h1 className="text-2xl font-light text-white mb-2">AltSlice · Residences</h1>
            <p className="text-gray-500 text-sm mb-6">Select a project to view details and invest</p>
            
            {/* Open/Funded Tabs */}
            <div className="mb-6 flex gap-4 border-b border-white/10">
              <button
                onClick={() => setOpportunityFilter('open')}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  opportunityFilter === 'open' 
                    ? 'text-emerald-400 border-b-2 border-emerald-400' 
                    : 'text-gray-500 hover:text-white'
                }`}
                data-testid="residences-tab-open"
              >
                Open ({openResidenceProperties.length})
              </button>
              <button
                onClick={() => setOpportunityFilter('funded')}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  opportunityFilter === 'funded' 
                    ? 'text-emerald-400 border-b-2 border-emerald-400' 
                    : 'text-gray-500 hover:text-white'
                }`}
                data-testid="residences-tab-funded"
              >
                Funded ({fundedResidenceProperties.length})
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-[#4ADE80] border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-500">Loading properties...</p>
              </div>
            ) : displayedResidenceProperties.length === 0 ? (
              <div className="text-center py-20 border border-white/10">
                <p className="text-gray-500 mb-2">
                  {opportunityFilter === 'open' ? 'No open residential opportunities' : 'No funded residential opportunities yet'}
                </p>
                <p className="text-xs text-gray-600">
                  {opportunityFilter === 'open' ? 'Check back soon for new opportunities' : 'Funded deals will appear here'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayedResidenceProperties.map(property => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onClick={() => handlePropertyClick(property.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      
      case 'orion':
        return <OrionFundTab user={user} />;
      
      case 'transactions':
        return <InvestorTransactionsTab user={user} />;
      
      case 'reports':
        return <InvestorReportsTab user={user} />;
      
      default:
        return <InvestorHomeTab user={user} setActiveTab={setActiveTab} />;
    }
  };

  // Determine which tab should be highlighted
  const getHighlightedTab = () => {
    if (selectedProperty) {
      const property = properties.find(p => p.id === selectedProperty);
      return property?.type === 'land' ? 'land' : 'residences';
    }
    return activeTab;
  };

  return (
    <div className="min-h-screen bg-black flex">
      <InvestorSidebar 
        activeTab={getHighlightedTab()} 
        setActiveTab={(tab) => {
          setActiveTab(tab);
          setSelectedProperty(null);
          setShowInvestmentFlow(false);
        }}
        user={user} 
        setUser={setUser} 
      />
      <main className="flex-1 ml-64">
        {renderContent()}
      </main>
      
      {/* Investment Booking Modal - ₹25,000 EOI → Document Signing → Final Payment */}
      {showInvestmentFlow && investmentData && (
        <InvestmentBookingModal
          isOpen={showInvestmentFlow}
          onClose={() => setShowInvestmentFlow(false)}
          propertyId={investmentData.propertyId}
          unitId={investmentData.unitId}
          userId={user?.id}
          onComplete={() => {
            setShowInvestmentFlow(false);
            setActiveTab('portfolio');
          }}
        />
      )}
    </div>
  );
}
